/**
 * Erros de conflito das rotas de profissional.
 *
 * A API devolve 409 em duas situações diferentes, e a interface reage a cada
 * uma de forma diferente: a duplicidade vira mensagem sob um campo específico
 * do formulário, os vínculos viram o estado bloqueado do diálogo de exclusão.
 * Um `Error` genérico com o texto do servidor não permitiria nem uma coisa nem
 * outra — daí as duas classes carregarem os dados estruturados do corpo.
 */
import { parseProblemJson } from '@/lib/api/problem'

/** Fonte única de verdade: o union type é inferido desta tupla, não repetido à mão. */
export const PROFESSIONAL_CONFLICT_FIELDS = ['email', 'phone', 'linkedinUrl'] as const
export type ProfessionalConflictField = (typeof PROFESSIONAL_CONFLICT_FIELDS)[number]

function isProfessionalConflictField(value: unknown): value is ProfessionalConflictField {
  return (
    typeof value === 'string' && (PROFESSIONAL_CONFLICT_FIELDS as readonly string[]).includes(value)
  )
}

export class DuplicateProfessionalError extends Error {
  readonly field: ProfessionalConflictField
  readonly professionalId: string
  readonly professionalName: string
  /**
   * O dono está arquivado (Story 36.5). Um arquivado saiu de circulação: o
   * cadastro rápido não lhe oferece «Usar este», e a frase diz onde desarquivá-lo.
   */
  readonly archived: boolean

  constructor(init: {
    message: string
    field: ProfessionalConflictField
    professionalId: string
    professionalName: string
    archived?: boolean
  }) {
    super(init.message)
    this.name = 'DuplicateProfessionalError'
    this.field = init.field
    this.professionalId = init.professionalId
    this.professionalName = init.professionalName
    this.archived = init.archived ?? false
  }
}

/**
 * O 409 da CORRIDA no índice único (Story 36.5): duas criações simultâneas
 * passaram ambas pela checagem prévia, e o servidor só sabe dizer que colidiu,
 * não com quem (`uniqueViolationProblem` de `professional-duplicates.ts`).
 *
 * Classe própria, e não um `Error` genérico, para o cadastro rápido mostrar a
 * frase do servidor — que é a verdade — em vez de um «Não foi possível criar»
 * que esconderia o duplicado. Decidida pelo CORPO, e não pela frase: comparar
 * texto partia na primeira mudança dela.
 */
export class ProfessionalIdentityRaceError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'ProfessionalIdentityRaceError'
  }
}

const CAMPO_DO_DUPLICADO: Record<ProfessionalConflictField, string> = {
  email: 'Este e-mail',
  phone: 'Este telefone',
  linkedinUrl: 'Este LinkedIn',
}

/**
 * A frase do duplicado no cadastro rápido — uma só, para o painel rápido e para
 * o cadastro completo embutido (Story 36.5, `SCREEN_PATTERNS.md` §3.3.3). Sem o
 * nome do dono, a frase do servidor: «já é de «»» não diria nada.
 */
export function fraseDoDuplicado(erro: DuplicateProfessionalError): string {
  if (erro.professionalName === '') return erro.message
  const base = `${CAMPO_DO_DUPLICADO[erro.field]} já é de «${erro.professionalName}»`
  return erro.archived ? `${base}, arquivado. Desarquive-o em Profissionais.` : `${base}.`
}

/** Pode-se escolher o dono no lugar do novo: está em circulação, e sabe-se quem é. */
export function podeUsarODono(erro: DuplicateProfessionalError): boolean {
  return !erro.archived && erro.professionalName !== ''
}

export interface LinkedOpportunity {
  applicationId: string
  title: string | null
}

function isLinkedOpportunity(value: unknown): value is LinkedOpportunity {
  if (typeof value !== 'object' || value === null) return false
  const obj = value as Record<string, unknown>
  return (
    typeof obj.applicationId === 'string' && (typeof obj.title === 'string' || obj.title === null)
  )
}

export class ProfessionalLinkedError extends Error {
  readonly applications: number
  readonly evaluations: number
  readonly resumes: number
  readonly opportunities: LinkedOpportunity[]
  /**
   * O servidor NOMEIA a saída (migration 0031): arquivar tira o profissional da
   * lista sem apagar o histórico que bloqueia a exclusão. Vem do 409 em vez de
   * ser deduzido aqui — uma dedução seria uma regra duplicada a envelhecer
   * sozinha do lado do browser.
   *
   * `false` quando o servidor não o disser (um bundle novo contra uma API
   * antiga): o diálogo cai no comportamento anterior, que é explicar o bloqueio
   * sem oferecer nada.
   */
  readonly canArchive: boolean

  constructor(init: {
    message: string
    applications: number
    evaluations: number
    resumes: number
    opportunities: LinkedOpportunity[]
    canArchive: boolean
  }) {
    super(init.message)
    this.name = 'ProfessionalLinkedError'
    this.applications = init.applications
    this.evaluations = init.evaluations
    this.resumes = init.resumes
    this.opportunities = init.opportunities
    this.canArchive = init.canArchive
  }
}

/**
 * Lê o problem+json e devolve o erro tipado correspondente. Nunca lança: um
 * corpo ilegível, `null`, ou de formato inesperado vira um `Error` genérico,
 * para o chamador ter sempre algo a relançar.
 *
 * A leitura segura do corpo (parse-as-object, `null` em vez de lançar) é
 * partilhada via `parseProblemJson` — a extração da MENSAGEM continua local
 * porque este parser precisa do corpo INTEIRO para os campos estruturados
 * abaixo (`conflictField`, `applications`, …), não só de uma string.
 */
export async function parseProfessionalConflict(res: Response): Promise<Error> {
  const fallback = `Falha na operação (${res.status})`
  const body = await parseProblemJson(res)
  if (!body) return new Error(fallback)

  const message = typeof body.detail === 'string' ? body.detail : fallback

  // ⚠️ `conflictingCandidateId` / `conflictingCandidateName` SÃO OS NOMES DO FIO,
  // E FICAM. A ponte bilíngue da API espelhava UM campo só — `candidateId` em
  // `professionalId` — e ela já foi levantada; estes dois NUNCA fizeram parte
  // dela. Nascem em `apps/api/src/lib/professional-duplicates.ts` e viajam com um
  // nome só: ler `body.conflictingProfessionalId` traria `undefined`, e o
  // formulário perderia em silêncio o link para o duplicado — sem erro de tipo
  // (o corpo é `Record<string, unknown>`) e sem teste vermelho.
  //
  // Os campos DA CLASSE já falam a língua nova: a troca de vocabulário é do lado
  // de cá da fronteira, e é aqui que ela acontece.
  if (
    isProfessionalConflictField(body.conflictField) &&
    typeof body.conflictingCandidateId === 'string'
  ) {
    return new DuplicateProfessionalError({
      message,
      field: body.conflictField,
      professionalId: body.conflictingCandidateId,
      professionalName:
        typeof body.conflictingCandidateName === 'string' ? body.conflictingCandidateName : '',
      archived: body.conflictingCandidateArchived === true,
    })
  }

  // A corrida: o índice único barrou a escrita, e o corpo não sabe dizer quem.
  if (body.title === 'Duplicate candidate' && typeof body.conflictingCandidateId !== 'string') {
    return new ProfessionalIdentityRaceError(message)
  }

  if (typeof body.applications === 'number') {
    return new ProfessionalLinkedError({
      message,
      applications: body.applications,
      evaluations: typeof body.evaluations === 'number' ? body.evaluations : 0,
      resumes: typeof body.resumes === 'number' ? body.resumes : 0,
      opportunities: Array.isArray(body.opportunities)
        ? body.opportunities.filter(isLinkedOpportunity)
        : [],
      canArchive: body.canArchive === true,
    })
  }

  return new Error(message)
}
